import { Ingredient, Step, CuisineType, FoodType } from "@/types/recipe";
import IngredientsList from "@/components/recipe/IngredientsList";
import StepsList from "@/components/recipe/StepsList";
import { BasicInfoSection } from "./form-sections/BasicInfoSection";
import { ImageSection } from "./form-sections/ImageSection";
import { VisibilitySection } from "./form-sections/VisibilitySection";
import { FormActions } from "./form-sections/FormActions";
import { Button } from "@/components/ui/button";
import { Calculator } from "lucide-react";
import NutritionalInfo from "@/components/recipe/NutritionalInfo";

interface RecipeFormProps {
  title: string;
  setTitle: (title: string) => void;
  description: string;
  setDescription: (description: string) => void;
  ingredients: Ingredient[];
  setIngredients: (ingredients: Ingredient[]) => void;
  steps: Step[];
  setSteps: (steps: Step[]) => void;
  visibility: "public" | "subscription";
  setVisibility: (visibility: "public" | "subscription") => void;
  imageFile: File | null;
  setImageFile: (file: File | null) => void;
  imagePreview: string | null;
  setImagePreview: (preview: string | null) => void;
  cuisineType: CuisineType | '';
  setCuisineType: (type: CuisineType | '') => void;
  foodType: FoodType | '';
  setFoodType: (type: FoodType | '') => void;
  servings: number;
  setServings: (servings: number) => void;
  onPreview: () => void;
  onSubmit: (e: React.FormEvent) => void;
  isSubmitting: boolean;
  onCalculateNutrition?: () => void;
  hasCalculatedNutrition?: boolean;
  nutritionalValues?: any;
  onNutritionalValuesEdit?: (values: any) => void;
}

export const RecipeForm = ({
  title,
  setTitle,
  description,
  setDescription,
  ingredients,
  setIngredients,
  steps,
  setSteps,
  visibility,
  setVisibility,
  imageFile,
  setImageFile,
  imagePreview,
  setImagePreview,
  cuisineType,
  setCuisineType,
  foodType,
  setFoodType,
  servings,
  setServings,
  onPreview,
  onSubmit,
  isSubmitting,
  onCalculateNutrition,
  hasCalculatedNutrition = false,
  nutritionalValues,
  onNutritionalValuesEdit,
}: RecipeFormProps) => {
  return (
    <form onSubmit={onSubmit} className="space-y-8">
      <BasicInfoSection
        title={title}
        setTitle={setTitle}
        description={description}
        setDescription={setDescription}
        cuisineType={cuisineType}
        setCuisineType={setCuisineType}
        foodType={foodType}
        setFoodType={setFoodType}
        servings={servings}
        setServings={setServings}
      />

      <ImageSection
        imageFile={imageFile}
        setImageFile={setImageFile}
        imagePreview={imagePreview}
        setImagePreview={setImagePreview}
      />

      <IngredientsList
        ingredients={ingredients}
        onIngredientChange={(index, ingredient) => {
          const newIngredients = [...ingredients];
          newIngredients[index] = ingredient;
          setIngredients(newIngredients);
        }}
        onAddIngredient={() => setIngredients([...ingredients, { amount: 0, unit: "cup", name: "" }])}
        onRemoveIngredient={(index) => setIngredients(ingredients.filter((_, i) => i !== index))}
        defaultServings={servings}
      />

      <StepsList
        steps={steps}
        onStepChange={(index, value) => {
          const newSteps = [...steps];
          newSteps[index] = { ...newSteps[index], ...value };
          setSteps(newSteps);
        }}
        onAddStep={() => setSteps([...steps, { instruction: "" }])}
        onRemoveStep={(index) => setSteps(steps.filter((_, i) => i !== index))}
      />

      <div className="space-y-4">
        <Button
          type="button"
          variant="outline"
          className="w-full"
          onClick={onCalculateNutrition}
        >
          <Calculator className="w-4 h-4 mr-2" />
          Calculate Nutritional Values
        </Button>

        {nutritionalValues && (
          <div className="mt-4">
            <NutritionalInfo 
              nutritionalValues={nutritionalValues}
              editable={true}
              onEdit={onNutritionalValuesEdit}
            />
          </div>
        )}
      </div>

      <VisibilitySection
        visibility={visibility}
        setVisibility={setVisibility}
      />

      <FormActions
        onPreview={onPreview}
        isSubmitting={isSubmitting}
        hasCalculatedNutrition={hasCalculatedNutrition}
      />
    </form>
  );
};
